import { useCallback, useEffect, useRef, useState } from "react";
import { localCache } from "../lib/cache";

// ─── In-memory query cache ─────────────────────────────────────────────────
// Survives component unmounts within the same browser session.
// Keyed by JSON.stringify(queryKey) → { data, ts }
// Capped at MEMORY_CACHE_MAX entries; evicts the oldest entry on overflow.
const queryMemoryCache = new Map();
const MEMORY_CACHE_MAX = 80;

function memoryCacheSet(queryKey, value) {
  if (!queryMemoryCache.has(queryKey) && queryMemoryCache.size >= MEMORY_CACHE_MAX) {
    let oldestKey = null;
    let oldestTs = Infinity;
    for (const [k, v] of queryMemoryCache) {
      if (v.ts < oldestTs) { oldestTs = v.ts; oldestKey = k; }
    }
    if (oldestKey) queryMemoryCache.delete(oldestKey);
  }
  queryMemoryCache.set(queryKey, value);
}

// ─── Subscriber registry ───────────────────────────────────────────────────
// key (string) → Set of refetch callbacks, used by invalidateQuery.
const querySubscribers = new Map();

function _localInvalidate(keyStr) {
  const subs = querySubscribers.get(keyStr);
  if (!subs) return;
  subs.forEach(cb => cb());
}

// BroadcastChannel: propagate cache invalidations to sibling tabs.
if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  if (!window.__kevesBroadcastChannel) {
    window.__kevesBroadcastChannel = new BroadcastChannel('keves_query_invalidation');
  }
  // Re-assign every module load so HMR doesn't leave a stale closure.
  window.__kevesBroadcastChannel.onmessage = ({ data }) => {
    if (data?.type === 'invalidate' && data?.key) {
      // Clear memory cache so components that mount during the refetch window
      // don't serve stale data while the background fetch is in-flight.
      queryMemoryCache.delete(data.key);
      _localInvalidate(data.key);
    }
  };
}

export function invalidateQuery(key) {
  const keyStr = Array.isArray(key) ? JSON.stringify(key) : key;
  // Clear in-memory cache so the next mount starts fresh instead of serving stale data.
  queryMemoryCache.delete(keyStr);
  _localInvalidate(keyStr);
  if (typeof window !== 'undefined' && window.__kevesBroadcastChannel) {
    try {
      window.__kevesBroadcastChannel.postMessage({ type: 'invalidate', key: keyStr });
    } catch {
      // Channel may be closed in edge cases — degrade silently.
    }
  }
}

// Invalidates all registered queries whose serialized key starts with the given prefix.
// Use for realtime: invalidateQueryPrefix(["church-allocations", districtId]) will hit
// all variants (with/without church_id, status filters) without knowing them upfront.
export function invalidateQueryPrefix(prefix) {
  // Build the prefix string: stringify and strip the trailing "]" so all longer
  // arrays with the same leading elements will match via startsWith.
  const prefixStr = (Array.isArray(prefix) ? JSON.stringify(prefix) : prefix).slice(0, -1);
  for (const keyStr of querySubscribers.keys()) {
    if (keyStr.startsWith(prefixStr)) {
      queryMemoryCache.delete(keyStr);
      _localInvalidate(keyStr);
    }
  }
}

// ─── useQueryCore ──────────────────────────────────────────────────────────
//
// Stale-while-revalidate behaviour:
//   • If in-memory cache exists for this key → render immediately, no skeleton,
//     then silently refresh from the network in the background.
//   • If only Dexie cache exists (cold start / page reload, cacheKey provided) →
//     render Dexie data within ~5 ms, then silently refresh from network.
//   • If neither cache exists → loading=true until first network response (old behaviour).
//
// cacheKey (optional): the string key used by localCache (Dexie) for this query.
//   Pass it from feature hooks that have a clean 1:1 mapping between their
//   queryFn and a single Dexie cache entry (e.g. families list, people list).
//   Omit for composite queries (PersonDetail) or filtered views (Visits by status).
//
export function useQueryCore({
  key,
  queryFn,
  enabled = true,
  sortFn,
  staleTime = 0,
  cacheKey,
}) {
  // queryKey must be computed before useState so the initial state can reference it.
  const queryKey = JSON.stringify(key);

  // Synchronous pre-hydration from in-memory cache (zero I/O cost, within same session).
  const initialEntry = queryMemoryCache.get(queryKey);
  const initialData = initialEntry?.data ?? null;

  const [data, setData] = useState(initialData ?? []);
  // Skip the skeleton entirely when we already have data to show.
  const [loading, setLoading] = useState(enabled && initialData === null);
  const [error, setError] = useState(null);

  const queryFnRef = useRef(queryFn);
  const sortFnRef = useRef(sortFn);
  const cacheKeyRef = useRef(cacheKey);
  // Seed from memory cache timestamp so staleTime works across navigations.
  const lastFetchAt = useRef(initialEntry?.ts ?? null);

  useEffect(() => { queryFnRef.current = queryFn; }, [queryFn]);
  useEffect(() => { sortFnRef.current = sortFn; }, [sortFn]);
  useEffect(() => { cacheKeyRef.current = cacheKey; }, [cacheKey]);

  // ── fetchData (exported as `refetch`) ──────────────────────────────────
  // Always runs as a background update: never shows a loading spinner.
  // Used for: explicit refetch() calls, invalidateQuery notifications, and
  // mutation rollbacks. The component already has data to display in all cases.
  const fetchData = useCallback(async () => {
    if (!enabled) return;
    setError(null);

    try {
      const result = await queryFnRef.current();
      const finalData = sortFnRef.current ? sortFnRef.current(result) : result;
      lastFetchAt.current = Date.now();
      memoryCacheSet(queryKey, { data: finalData, ts: Date.now() });
      setData(finalData);
    } catch (err) {
      setError(err?.message ?? "Error al cargar");
    } finally {
      // Always clear loading — handles the case where this fires during the
      // initial mount before the main effect's run() has finished.
      setLoading(false);
    }
  }, [enabled, queryKey]);

  // ── Initial fetch + SWR effect ─────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    if (
      staleTime > 0 &&
      lastFetchAt.current &&
      Date.now() - lastFetchAt.current < staleTime
    ) {
      return; // Data is still fresh — skip network round-trip entirely.
    }

    let cancelled = false;

    const run = async () => {
      const hasMemoryCache = queryMemoryCache.has(queryKey);

      // Show skeleton only when there is no cached data to display.
      if (!hasMemoryCache) {
        setLoading(true);
      }
      setError(null);

      // ── Phase 0: Dexie cold-start pre-hydration ───────────────────────
      // Runs only on the first load after a page reload (no in-memory cache)
      // when the caller provided a Dexie cache key.  Resolves in ~5 ms and
      // hides the skeleton immediately with stale-but-useful data.
      if (!hasMemoryCache && cacheKeyRef.current) {
        try {
          const dexieData = await localCache.get(cacheKeyRef.current);
          if (!cancelled && dexieData != null && !queryMemoryCache.has(queryKey)) {
            const finalData = sortFnRef.current
              ? sortFnRef.current(dexieData)
              : dexieData;
            setData(finalData);
            setLoading(false); // Data is available — hide skeleton now.
          }
        } catch {
          // Dexie read failure is non-fatal; Phase 1 always runs.
        }
      }

      // ── Phase 1: Network fetch ────────────────────────────────────────
      // Always executes regardless of Phase 0 outcome.
      // If Phase 0 already rendered stale data this becomes a silent refresh.
      try {
        const result = await queryFnRef.current();
        if (cancelled) return;
        const finalData = sortFnRef.current ? sortFnRef.current(result) : result;
        lastFetchAt.current = Date.now();
        memoryCacheSet(queryKey, { data: finalData, ts: Date.now() });
        setData(finalData);
      } catch (err) {
        if (!cancelled) setError(err?.message ?? "Error al cargar");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [queryKey, enabled, staleTime]);

  // ── Subscription for invalidateQuery / BroadcastChannel ───────────────
  useEffect(() => {
    const notify = () => {
      lastFetchAt.current = null;
      fetchData(); // background — no loading spinner
    };

    if (!querySubscribers.has(queryKey)) {
      querySubscribers.set(queryKey, new Set());
    }

    const subs = querySubscribers.get(queryKey);
    subs.add(notify);

    return () => {
      subs.delete(notify);
      if (subs.size === 0) {
        querySubscribers.delete(queryKey);
      }
    };
  }, [queryKey, fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    setData,
  };
}
